import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useGenerationRuns } from '@/hooks/useGenerationRuns';

interface GenerationRunsTableProps {
  mapId: string;
}

export function GenerationRunsTable({ mapId }: GenerationRunsTableProps) {
  const { runs, loading } = useGenerationRuns(mapId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) {
    return <p className="text-sm text-muted-foreground py-4">Ładowanie logów AI...</p>;
  }

  if (runs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        Brak logów generacji AI.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[30px]" />
          <TableHead>Step</TableHead>
          <TableHead className="w-[80px]">Model</TableHead>
          <TableHead className="w-[80px]">Status</TableHead>
          <TableHead className="w-[80px]">Tokeny</TableHead>
          <TableHead className="w-[70px]">Koszt</TableHead>
          <TableHead className="w-[70px]">Czas</TableHead>
          <TableHead className="w-[140px]">Data</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <Collapsible key={run.id} asChild open={expandedId === run.id}>
            <>
              <CollapsibleTrigger asChild>
                <TableRow
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpandedId(expandedId === run.id ? null : run.id)}
                >
                  <TableCell>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedId === run.id ? 'rotate-180' : ''}`} />
                  </TableCell>
                  <TableCell className="font-medium text-sm">{run.step}</TableCell>
                  <TableCell className="text-xs">{run.model}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        run.status === 'completed' ? 'default' :
                        run.status === 'failed' ? 'destructive' : 'secondary'
                      }
                      className="text-xs"
                    >
                      {run.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {run.tokens_input + run.tokens_output}
                  </TableCell>
                  <TableCell className="text-xs">
                    ${run.cost_usd.toFixed(4)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(run.duration_ms / 1000).toFixed(1)}s
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(run.created_at).toLocaleString('pl-PL')}
                  </TableCell>
                </TableRow>
              </CollapsibleTrigger>
              <CollapsibleContent asChild>
                <TableRow>
                  <TableCell colSpan={8} className="p-0">
                    <div className="p-4 bg-muted/30 space-y-3">
                      {run.error_message && (
                        <div>
                          <p className="text-xs font-medium text-destructive mb-1">Error:</p>
                          <pre className="text-xs bg-destructive/10 p-2 rounded overflow-x-auto">
                            {run.error_message}
                          </pre>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium mb-1">Input:</p>
                          <pre className="text-xs bg-background p-2 rounded max-h-[200px] overflow-auto border">
                            {JSON.stringify(run.input_json, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <p className="text-xs font-medium mb-1">Output:</p>
                          <pre className="text-xs bg-background p-2 rounded max-h-[200px] overflow-auto border">
                            {run.output_json ? JSON.stringify(run.output_json, null, 2) : '—'}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </CollapsibleContent>
            </>
          </Collapsible>
        ))}
      </TableBody>
    </Table>
  );
}
